/**
 * SALTWORKS — DISCOVERY SET LP 2026-02-11
 * "The Conversion Beast" — JavaScript Interactions
 * Updated: 2026-02-13 with Lightbox
 */

(function() {
  'use strict';

  // ─── Utility: Throttle ───
  function throttle(fn, wait) {
    let lastTime = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, args);
      }
    };
  }

  // ─── Utility: Intersection Observer for Reveal ───
  function initReveal() {
    const elements = document.querySelectorAll('[data-reveal]');
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach(el => observer.observe(el));
  }

  // ─── Hero Image Carousel ───
  function initHeroCarousel() {
    const carousel = document.querySelector('[data-carousel]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('[data-slide]');
    const dots = document.querySelectorAll('[data-dot]');
    let current = 0;
    let autoplayTimer;

    function goTo(index) {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
      current = index;
    }

    function next() {
      goTo((current + 1) % slides.length);
    }

    function prev() {
      goTo((current - 1 + slides.length) % slides.length);
    }

    function startAutoplay() {
      autoplayTimer = setInterval(next, 4000);
    }

    function stopAutoplay() {
      clearInterval(autoplayTimer);
    }

    // Dot clicks
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        goTo(i);
        startAutoplay();
      });
    });

    // Swipe support
    let touchStartX = 0;
    carousel.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      stopAutoplay();
    }, { passive: true });

    carousel.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          goTo((current + 1) % slides.length);
        } else {
          goTo((current - 1 + slides.length) % slides.length);
        }
      }
      startAutoplay();
    }, { passive: true });

    // ─── Lightbox for Desktop ───
    let lightbox = null;
    let lightboxCurrent = 0;

    function createLightbox() {
      if (lightbox) return;

      lightbox = document.createElement('div');
      lightbox.className = 'ds-lightbox';
      lightbox.innerHTML = `
        <div class="ds-lightbox__overlay"></div>
        <div class="ds-lightbox__content">
          <button class="ds-lightbox__close" aria-label="Close">&times;</button>
          <button class="ds-lightbox__prev" aria-label="Previous">&#8249;</button>
          <button class="ds-lightbox__next" aria-label="Next">&#8250;</button>
          <div class="ds-lightbox__image-wrap">
            <img class="ds-lightbox__image" src="" alt="">
          </div>
          <div class="ds-lightbox__dots"></div>
        </div>
      `;
      document.body.appendChild(lightbox);

      // Create dots
      const dotsContainer = lightbox.querySelector('.ds-lightbox__dots');
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'ds-lightbox__dot';
        dot.addEventListener('click', () => lightboxGoTo(i));
        dotsContainer.appendChild(dot);
      });

      // Event listeners
      lightbox.querySelector('.ds-lightbox__overlay').addEventListener('click', closeLightbox);
      lightbox.querySelector('.ds-lightbox__close').addEventListener('click', closeLightbox);
      lightbox.querySelector('.ds-lightbox__prev').addEventListener('click', () => {
        lightboxGoTo((lightboxCurrent - 1 + slides.length) % slides.length);
      });
      lightbox.querySelector('.ds-lightbox__next').addEventListener('click', () => {
        lightboxGoTo((lightboxCurrent + 1) % slides.length);
      });

      // Click on image to go to next
      const lightboxImg = lightbox.querySelector('.ds-lightbox__image');
      lightboxImg.addEventListener('click', () => {
        lightboxGoTo((lightboxCurrent + 1) % slides.length);
      });
      lightboxImg.style.cursor = 'pointer';

      // Keyboard navigation
      document.addEventListener('keydown', handleLightboxKeydown);

      // Swipe/drag support for lightbox
      const imageWrap = lightbox.querySelector('.ds-lightbox__image-wrap');
      let touchStartX = 0;
      let mouseStartX = 0;
      let isDragging = false;

      // Touch events (mobile)
      imageWrap.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });

      imageWrap.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            lightboxGoTo((lightboxCurrent + 1) % slides.length);
          } else {
            lightboxGoTo((lightboxCurrent - 1 + slides.length) % slides.length);
          }
        }
      }, { passive: true });

      // Mouse drag events (desktop)
      imageWrap.style.cursor = 'grab';

      imageWrap.addEventListener('mousedown', (e) => {
        if (e.target.closest('.ds-lightbox__prev') || e.target.closest('.ds-lightbox__next')) return;
        isDragging = true;
        mouseStartX = e.clientX;
        imageWrap.style.cursor = 'grabbing';
        e.preventDefault();
      });

      imageWrap.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        e.preventDefault();
      });

      imageWrap.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        isDragging = false;
        imageWrap.style.cursor = 'grab';
        const diff = mouseStartX - e.clientX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            lightboxGoTo((lightboxCurrent + 1) % slides.length);
          } else {
            lightboxGoTo((lightboxCurrent - 1 + slides.length) % slides.length);
          }
        }
      });

      imageWrap.addEventListener('mouseleave', () => {
        if (isDragging) {
          isDragging = false;
          imageWrap.style.cursor = 'grab';
        }
      });
    }

    function handleLightboxKeydown(e) {
      if (!lightbox || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') lightboxGoTo((lightboxCurrent + 1) % slides.length);
      if (e.key === 'ArrowLeft') lightboxGoTo((lightboxCurrent - 1 + slides.length) % slides.length);
    }

    function lightboxGoTo(index) {
      lightboxCurrent = index;
      const img = slides[index].querySelector('img');
      const lightboxImg = lightbox.querySelector('.ds-lightbox__image');
      // Use larger image for lightbox
      const src = img.src.replace(/\d+x\d+/, '1200x1200');
      lightboxImg.src = src;
      lightboxImg.alt = img.alt;

      // Update dots
      lightbox.querySelectorAll('.ds-lightbox__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    function hideChat() {
      const chat = document.querySelector('inbox-online-store-chat, #ShopifyChat, #inbox-online-store-chat');
      if (chat) chat.style.setProperty('z-index', '0', 'important');
    }
    function showChat() {
      const chat = document.querySelector('inbox-online-store-chat, #ShopifyChat, #inbox-online-store-chat');
      if (chat) chat.style.setProperty('z-index', '2147483647', 'important');
    }

    function openLightbox(index) {
      createLightbox();
      lightboxGoTo(index);
      lightbox.classList.add('is-open');
      document.body.classList.add('lightbox-open');
      document.body.style.overflow = 'hidden';
      hideChat();
      stopAutoplay();
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.classList.remove('lightbox-open');
      document.body.style.overflow = '';
      showChat();
      startAutoplay();
    }

    // Click handler - tap opens lightbox (both mobile and desktop)
    // Swipe handles navigation (touchstart/touchend above)
    carousel.addEventListener('click', (e) => {
      if (e.target.closest('[data-dot]')) return;
      openLightbox(current);
    });

    // Add cursor pointer
    carousel.style.cursor = 'pointer';

    // Custom event listener for sticky bar thumbnail
    carousel.addEventListener('openLightbox', (e) => {
      const index = e.detail?.index || 0;
      openLightbox(index);
    });

    startAutoplay();
  }

  // ─── Quantity Selector ───
  function initQuantity() {
    const minusBtn = document.querySelector('[data-qty-minus]');
    const plusBtn = document.querySelector('[data-qty-plus]');
    const valueEl = document.querySelector('[data-qty-value]');
    const inputEl = document.querySelector('[data-qty-input]');
    const priceEl = document.querySelector('[data-price]');

    if (!minusBtn || !plusBtn || !valueEl) return;

    const basePrice = 29;
    let qty = 1;

    function update() {
      valueEl.textContent = qty;
      if (inputEl) inputEl.value = qty;
      if (priceEl) priceEl.textContent = '£' + (basePrice * qty);
    }

    minusBtn.addEventListener('click', () => {
      if (qty > 1) {
        qty--;
        update();
      }
    });

    plusBtn.addEventListener('click', () => {
      if (qty < 10) {
        qty++;
        update();
      }
    });
  }

  // ─── Fragrance Filter & Accordion ───
  function initFragrances() {
    const filters = document.querySelectorAll('[data-filter]');
    const frags = document.querySelectorAll('[data-frag]');

    // Filter tabs
    filters.forEach(btn => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.filter;

        filters.forEach(f => f.classList.remove('is-active'));
        btn.classList.add('is-active');

        frags.forEach(frag => {
          if (filter === 'all') {
            frag.classList.remove('is-hidden');
          } else {
            const family = frag.dataset.family;
            frag.classList.toggle('is-hidden', family !== filter);
          }
        });
      });
    });

    // Accordion expand - only header toggles, clicking inside details doesn't close
    frags.forEach(frag => {
      const header = frag.querySelector('.ds-frag__header');
      if (!header) return;

      header.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = frag.classList.contains('is-expanded');

        // Close all others
        frags.forEach(f => f.classList.remove('is-expanded'));

        // Toggle current
        if (!isExpanded) {
          frag.classList.add('is-expanded');

          // Scroll to the expanded card after a short delay (for animation)
          setTimeout(() => {
            const headerRect = frag.getBoundingClientRect();
            const offset = 20; // Small offset from top

            // Only scroll if the header is above the viewport or content goes below
            if (headerRect.top < offset || headerRect.top > window.innerHeight * 0.4) {
              frag.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Adjust for any fixed headers
              setTimeout(() => {
                window.scrollBy({ top: -offset, behavior: 'smooth' });
              }, 300);
            }
          }, 50);
        }
      });
    });
  }

  // ─── Highlight Reviews Carousel ───
  function initHighlightCarousel() {
    const carousel = document.querySelector('[data-highlight-carousel]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.ds-highlight__slide');
    const dots = carousel.querySelectorAll('[data-highlight-dot]');
    let current = 0;
    let autoplayTimer;
    let touchStartX = 0;

    function goTo(index) {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
      current = index;
    }

    function next() {
      goTo((current + 1) % slides.length);
    }

    function prev() {
      goTo((current - 1 + slides.length) % slides.length);
    }

    function startAutoplay() {
      autoplayTimer = setInterval(next, 5000);
    }

    function stopAutoplay() {
      clearInterval(autoplayTimer);
    }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        goTo(i);
        startAutoplay();
      });
    });

    // Swipe support for mobile
    carousel.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      stopAutoplay();
    }, { passive: true });

    carousel.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          next(); // Swipe left = next
        } else {
          prev(); // Swipe right = previous
        }
      }
      startAutoplay();
    }, { passive: true });

    // Drag support for desktop
    let mouseStartX = 0;
    let isDragging = false;

    carousel.style.cursor = 'grab';

    carousel.addEventListener('mousedown', (e) => {
      if (e.target.closest('[data-highlight-dot]')) return;
      isDragging = true;
      mouseStartX = e.clientX;
      carousel.style.cursor = 'grabbing';
      stopAutoplay();
      e.preventDefault();
    });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
    });

    carousel.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      carousel.style.cursor = 'grab';
      const diff = mouseStartX - e.clientX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          next();
        } else {
          prev();
        }
      }
      startAutoplay();
    });

    carousel.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        carousel.style.cursor = 'grab';
        startAutoplay();
      }
    });

    startAutoplay();
  }

  // ─── FAQ Accordion ───
  function initAccordion() {
    const toggles = document.querySelectorAll('[data-accordion-toggle]');

    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const item = toggle.closest('.ds-accordion__item');
        const isOpen = item.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.ds-accordion__item').forEach(i => {
          i.classList.remove('is-open');
        });

        // Toggle current
        if (!isOpen) {
          item.classList.add('is-open');
        }
      });
    });
  }

  // ─── Sticky Bar ───
  function initStickyBar() {
    const hero = document.getElementById('ds-hero');
    const sticky = document.getElementById('ds-sticky');

    if (!hero || !sticky) return;

    const onScroll = throttle(() => {
      const heroBottom = hero.getBoundingClientRect().bottom;
      sticky.classList.toggle('is-visible', heroBottom < -100);
    }, 100);

    window.addEventListener('scroll', onScroll, { passive: true });

    // Sticky thumbnail opens lightbox (both mobile and desktop)
    const stickyThumb = sticky.querySelector('.ds-sticky__thumb');
    if (stickyThumb) {
      stickyThumb.style.cursor = 'pointer';
      stickyThumb.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Open lightbox on both mobile and desktop
        const carousel = document.querySelector('[data-carousel]');
        if (carousel) {
          // Simulate click on carousel to open lightbox
          // But we need to bypass the mobile check in carousel click handler
          // So we dispatch a custom event
          const event = new CustomEvent('openLightbox', { detail: { index: 1 } });
          carousel.dispatchEvent(event);
        }
      });
    }
  }

  // ─── Scroll to Buy ───
  function initScrollToBuy() {
    const buttons = document.querySelectorAll('[data-scroll-to-buy]');
    const buybox = document.querySelector('.ds-buybox');

    if (!buttons.length || !buybox) return;

    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        buybox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  // ─── Add to Cart (AJAX) ───
  function initAddToCart() {
    const form = document.getElementById('ds-add-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = form.querySelector('button[type="submit"]');
      const originalText = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>Adding...</span>';

      try {
        const formData = new FormData(form);
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          btn.innerHTML = '<span>Added!</span>';

          // Trigger cart drawer or redirect
          if (typeof window.theme !== 'undefined' && window.theme.cart) {
            window.theme.cart.open();
          } else {
            // Update cart count if available
            const cartCountEls = document.querySelectorAll('[data-cart-count]');
            if (cartCountEls.length) {
              const cartRes = await fetch(window.theme.routes.cart);
              const cart = await cartRes.json();
              cartCountEls.forEach(el => {
                el.textContent = cart.item_count;
              });
            }
          }

          setTimeout(() => {
            btn.disabled = false;
            btn.innerHTML = originalText;
          }, 2000);
        } else {
          throw new Error('Add to cart failed');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        btn.innerHTML = '<span>Error - Try Again</span>';
        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalText;
        }, 2000);
      }
    });
  }

  // ─── Final CTA Add to Cart (clicks main button without scroll) ───
  function initFinalAddToCart() {
    const finalBtn = document.querySelector('[data-final-add-to-cart]');
    const form = document.getElementById('ds-add-form');
    if (!finalBtn || !form) return;

    finalBtn.addEventListener('click', (e) => {
      e.preventDefault();
      // Save scroll position, click, restore
      const scrollY = window.scrollY;
      form.querySelector('button[type="submit"]')?.click();
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    });
  }

  // ─── Smooth Scroll for Anchor Links ───
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  // ─── Sticky Bar Add to Cart ───
  function initStickyAddToCart() {
    const stickyBtn = document.querySelector('[data-sticky-atc]');
    if (!stickyBtn) return;

    stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Find and click the main Add to Cart button
      const mainForm = document.getElementById('ds-add-form');
      if (mainForm) {
        const mainBtn = mainForm.querySelector('button[type="submit"]');
        if (mainBtn) {
          mainBtn.click();
          return;
        }
      }

      // Fallback: try any product form submit button
      const fallbackBtn = document.querySelector('.ds-buybox form button[type="submit"], .ds-buybox__atc');
      if (fallbackBtn) {
        fallbackBtn.click();
      }
    });
  }

  // ─── Desktop Drag Scroll for Carousels ───
  function initDragScroll() {
    const scrollContainers = document.querySelectorAll('.ds-proof__scroll, .ds-hero__carousel');

    scrollContainers.forEach(container => {
      let isDown = false;
      let startX;
      let scrollLeft;

      container.style.cursor = 'grab';

      container.addEventListener('mousedown', (e) => {
        // Don't interfere with links/buttons
        if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') return;

        isDown = true;
        container.style.cursor = 'grabbing';
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        e.preventDefault();
      });

      container.addEventListener('mouseleave', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mouseup', () => {
        isDown = false;
        container.style.cursor = 'grab';
      });

      container.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 1.5; // Scroll speed multiplier
        container.scrollLeft = scrollLeft - walk;
      });
    });
  }

  // ─── Font Size Pinch Control (Accessibility) ───
  function initPinchFontScale() {
    // Only enable on touch devices
    if (!('ontouchstart' in window)) return;

    const indicator = document.getElementById('ds-font-indicator');
    const sizeDisplay = indicator?.querySelector('[data-font-size]');
    const body = document.body;

    if (!indicator || !sizeDisplay) return;

    // Load saved scale from localStorage
    let currentScale = parseFloat(localStorage.getItem('ds-font-scale') || '1');
    const minScale = 0.8;  // 80%
    const maxScale = 1.5;  // 150%

    // Apply initial scale if different from default
    if (currentScale !== 1) {
      applyScale(currentScale, false);
    }

    let initialDistance = 0;
    let initialScale = currentScale;
    let isPinching = false;
    let hideTimeout;

    function getDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function showIndicator() {
      clearTimeout(hideTimeout);
      indicator.classList.add('is-visible');
    }

    function hideIndicator() {
      hideTimeout = setTimeout(() => {
        indicator.classList.remove('is-visible');
      }, 1000);
    }

    function applyScale(scale, animate = true) {
      currentScale = Math.min(maxScale, Math.max(minScale, scale));

      // Update display
      const percent = Math.round(currentScale * 100);
      sizeDisplay.textContent = percent + '%';

      // Apply CSS variable for font scaling
      document.documentElement.style.setProperty('--ds-font-multiplier', currentScale);
      body.classList.add('ds-font-scaled');

      // Save to localStorage
      localStorage.setItem('ds-font-scale', currentScale.toString());
    }

    // Touch event handlers
    document.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        // Don't interfere with lightbox or carousel
        if (document.querySelector('.ds-lightbox.is-open')) return;

        isPinching = true;
        initialDistance = getDistance(e.touches);
        initialScale = currentScale;
        showIndicator();
      }
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      if (!isPinching || e.touches.length !== 2) return;

      const currentDistance = getDistance(e.touches);
      const scaleFactor = currentDistance / initialDistance;
      const newScale = initialScale * scaleFactor;

      applyScale(newScale);
    }, { passive: true });

    document.addEventListener('touchend', (e) => {
      if (isPinching && e.touches.length < 2) {
        isPinching = false;
        hideIndicator();
      }
    }, { passive: true });

    document.addEventListener('touchcancel', () => {
      isPinching = false;
      hideIndicator();
    }, { passive: true });

    // Double-tap to reset (optional accessibility feature)
    let lastTap = 0;
    document.addEventListener('touchend', (e) => {
      // Only on single finger tap
      if (e.changedTouches.length !== 1) return;

      const now = Date.now();
      const timeDiff = now - lastTap;

      // Double tap within 300ms on the indicator area resets scale
      if (timeDiff < 300 && timeDiff > 0) {
        const touch = e.changedTouches[0];
        const rect = indicator.getBoundingClientRect();

        // Check if tap was near center of screen (indicator area)
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const tapDist = Math.sqrt(
          Math.pow(touch.clientX - centerX, 2) +
          Math.pow(touch.clientY - centerY, 2)
        );

        if (tapDist < 100 && currentScale !== 1) {
          applyScale(1);
          showIndicator();
          hideIndicator();
        }
      }

      lastTap = now;
    }, { passive: true });
  }

  // ─── Initialize All ───
  function init() {
    initReveal();
    initHeroCarousel();
    initQuantity();
    initFragrances();
    initHighlightCarousel();
    initAccordion();
    initStickyBar();
    initScrollToBuy();
    initAddToCart();
    initFinalAddToCart();
    initStickyAddToCart();
    initSmoothScroll();
    initReviewPhotos();
    initFragReviewsCarousel();
    initDragScroll();
    initPinchFontScale();
  }

  // Review Photo Toggle
  function initReviewPhotos() {
    document.querySelectorAll('[data-review-photo]').forEach(photo => {
      photo.addEventListener('click', () => {
        photo.classList.toggle('is-expanded');
      });
    });
  }

  // Fragrance Reviews Carousel (auto-rotate every 4 seconds + swipe support)
  function initFragReviewsCarousel() {
    document.querySelectorAll('[data-frag-reviews]').forEach(carousel => {
      const reviews = carousel.querySelectorAll('.ds-frag__review');
      const dots = carousel.querySelectorAll('[data-review-dot]');
      if (reviews.length <= 1) return;

      let current = 0;
      let autoplayTimer;
      let touchStartX = 0;
      let touchEndX = 0;

      function goTo(index) {
        reviews[current].classList.remove('is-active');
        dots[current].classList.remove('is-active');
        current = index;
        reviews[current].classList.add('is-active');
        dots[current].classList.add('is-active');
      }

      function next() {
        goTo((current + 1) % reviews.length);
      }

      function prev() {
        goTo((current - 1 + reviews.length) % reviews.length);
      }

      function startAutoplay() {
        autoplayTimer = setInterval(next, 4000);
      }

      function stopAutoplay() {
        clearInterval(autoplayTimer);
      }

      // Dot clicks
      dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent fragrance accordion toggle
          stopAutoplay();
          goTo(i);
          startAutoplay();
        });
      });

      // Swipe support for mobile
      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        stopAutoplay();
      }, { passive: true });

      carousel.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > 40) { // Min swipe distance
          e.stopPropagation(); // Prevent accordion toggle on swipe
          if (diff > 0) {
            next(); // Swipe left = next
          } else {
            prev(); // Swipe right = previous
          }
        }
        startAutoplay();
      }, { passive: true });

      // Drag support for desktop
      let mouseStartX = 0;
      let isDragging = false;

      carousel.style.cursor = 'grab';

      carousel.addEventListener('mousedown', (e) => {
        if (e.target.closest('[data-review-dot]')) return; // Don't drag on dots
        isDragging = true;
        mouseStartX = e.clientX;
        carousel.style.cursor = 'grabbing';
        stopAutoplay();
        e.preventDefault();
      });

      carousel.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        e.preventDefault();
      });

      carousel.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        isDragging = false;
        carousel.style.cursor = 'grab';
        const diff = mouseStartX - e.clientX;

        if (Math.abs(diff) > 40) {
          e.stopPropagation();
          if (diff > 0) {
            next();
          } else {
            prev();
          }
        }
        startAutoplay();
      });

      carousel.addEventListener('mouseleave', () => {
        if (isDragging) {
          isDragging = false;
          carousel.style.cursor = 'grab';
          startAutoplay();
        }
      });

      startAutoplay();
    });
  }

  // Fix Oud Harmony image (CDN cache workaround)
  function fixOudHarmonyImage() {
    const oudHarmonyImg = 'https://cdn.shopify.com/s/files/1/0653/7091/5035/files/OUDHARMONY_bd101df8-fc8c-42e4-9067-7e4f1c82f42a.jpg';
    document.querySelectorAll('.ds-frag').forEach(frag => {
      const name = frag.querySelector('.ds-frag__name');
      if (name && name.textContent.toLowerCase().includes('oud harmony')) {
        // Fix swatch image
        const swatchImg = frag.querySelector('.ds-frag__swatch img');
        if (swatchImg && swatchImg.src.includes('no-image')) {
          swatchImg.src = oudHarmonyImg;
          swatchImg.style.objectFit = 'cover';
          swatchImg.style.borderRadius = '50%';
        }
        // Fix details image
        const detailImg = frag.querySelector('.ds-frag__image img');
        if (detailImg && detailImg.src.includes('no-image')) {
          detailImg.src = oudHarmonyImg;
          detailImg.style.objectFit = 'cover';
        }
      }
    });
  }

  // ─── Fix Loox iframe height on mobile ───
  function fixLooxIframeHeight() {
    if (window.innerWidth > 480) return;

    const iframe = document.querySelector('#looxReviewsFrame');
    const looxWidget = document.querySelector('.loox-widget');
    if (!iframe) return;

    // Loox sets wrong height on mobile - cap it at reasonable max
    const maxHeight = 5500;

    const checkHeight = () => {
      const currentHeight = parseInt(iframe.style.height) || iframe.offsetHeight;
      if (currentHeight > 6000) {
        iframe.style.height = maxHeight + 'px';
        // Also fix parent containers
        if (looxWidget) looxWidget.style.height = maxHeight + 'px';
      }
    };

    // Check initially and observe for changes
    checkHeight();

    const observer = new MutationObserver(checkHeight);
    observer.observe(iframe, { attributes: true, attributeFilter: ['style', 'height'] });

    // Also check after Loox might load more
    iframe.addEventListener('load', checkHeight);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      fixOudHarmonyImage();
      setTimeout(fixLooxIframeHeight, 3000);
    });
  } else {
    init();
    fixOudHarmonyImage();
    setTimeout(fixLooxIframeHeight, 3000);
  }

})();
